import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useDemo } from "../context/DemoContext";
import { metenApi } from "../services/metenApi";
import { normalizeAdminFlag } from "../utils/adminUsers";
import {
  getCatalogWriteDisabledMessage,
  getTurbineWriteDisabledMessage,
  isDemoExpired,
  isDemoModeEnabled,
  mergeDemoProfiles,
  normalizeDemoProfile,
} from "../utils/demoAccess";

export function useCurrentUserAccess() {
  const { user, isSessionReady } = useAuth();
  const { demoProfile, demoLoading, demoError } = useDemo();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");

  useEffect(() => {
    let active = true;

    if (!isSessionReady || !user?.id) {
      setProfile(null);
      setProfileError("");
      setProfileLoading(false);
      return () => {
        active = false;
      };
    }

    setProfileLoading(true);
    metenApi
      .getProfile()
      .then((payload) => {
        if (!active) return;
        setProfile(payload || null);
        setProfileError("");
      })
      .catch((err) => {
        if (!active) return;
        setProfile(null);
        setProfileError(err?.message || "Failed to load profile.");
      })
      .finally(() => {
        if (!active) return;
        setProfileLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isSessionReady, user?.id]);

  const isAdminUser = useMemo(() => normalizeAdminFlag(profile, user), [profile, user]);
  const profileDemoProfile = useMemo(() => normalizeDemoProfile(profile), [profile]);
  const effectiveDemoProfile = useMemo(
    () => mergeDemoProfiles(profileDemoProfile, demoProfile),
    [demoProfile, profileDemoProfile]
  );
  const isDemoUser = isDemoModeEnabled(effectiveDemoProfile);
  const accessLoading = !isSessionReady || profileLoading || demoLoading;
  const catalogWriteDisabledMessage = getCatalogWriteDisabledMessage({
    isAdminUser,
    demoProfile: effectiveDemoProfile,
    accessLoading,
    demoStatusKnown: !demoError,
  });
  const turbineWriteDisabledMessage = getTurbineWriteDisabledMessage({
    isAdminUser,
    demoProfile: effectiveDemoProfile,
    accessLoading,
    demoStatusKnown: !demoError,
  });

  return {
    profile,
    profileLoading,
    profileError,
    demoError,
    accessLoading,
    isAdminUser,
    isDemoUser,
    isDemoLocked: Boolean(effectiveDemoProfile?.demoLocked),
    isDemoExpired: isDemoExpired(effectiveDemoProfile),
    demoProfile: effectiveDemoProfile,
    catalogWriteAllowed: !catalogWriteDisabledMessage,
    catalogWriteDisabledMessage,
    turbineWriteAllowed: !turbineWriteDisabledMessage,
    turbineWriteDisabledMessage,
  };
}
